import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Mic } from "lucide-react";
import { useDeepgram } from "@/hooks/useDeepgram";
import { DEEPGRAM_API_KEY } from "@/config/api";
import { MicrophoneErrorDialog } from "@/components/MicrophoneErrorDialog";

interface MicrophoneSectionProps {
  selectedMicrophone?: string;
  onMicrophoneChange: (deviceId: string) => void;
}

export const MicrophoneSection = ({ selectedMicrophone, onMicrophoneChange }: MicrophoneSectionProps) => {
  const { t } = useTranslation();
  const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [microphoneErrorOpen, setMicrophoneErrorOpen] = useState(false);

  const getMicrophones = useCallback(async () => {
    try {
      console.log("🎤 [CONFIG] Starting microphone enumeration...");
      console.log("🎤 [CONFIG] Navigator.mediaDevices available:", !!navigator.mediaDevices);
      console.log("🎤 [CONFIG] getUserMedia available:", !!navigator.mediaDevices?.getUserMedia);
      
      // First check if media devices are available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("❌ [CONFIG] getUserMedia not supported");
        setMicrophoneErrorOpen(true);
        return;
      }

      // Request permission first
      console.log("🎤 [CONFIG] Requesting microphone permission...");
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Now enumerate devices
      console.log("🎤 [CONFIG] Enumerating devices...");
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log("🎤 [CONFIG] All devices found:", devices.length);
      
      const microphones = devices.filter(device => device.kind === 'audioinput');
      console.log("🎤 [CONFIG] Audio input devices:", microphones);
      console.log("🎤 [CONFIG] Microphones with labels:", microphones.map(m => ({ id: m.deviceId, label: m.label })));
      
      // Check if we have actual working microphones (not just empty entries)
      const workingMicrophones = microphones.filter(mic => 
        mic.deviceId && mic.deviceId !== '' && mic.deviceId !== 'default'
      );
      
      console.log("🎤 [CONFIG] Working microphones:", workingMicrophones.length);
      
      if (workingMicrophones.length === 0) {
        console.log("🎤 [CONFIG] No working microphones found - opening error dialog");
        setMicrophoneErrorOpen(true);
        setAvailableMicrophones([]);
        return;
      }
      
      setAvailableMicrophones(microphones);
      
      if (microphones.length > 0 && !selectedMicrophone) {
        onMicrophoneChange(microphones[0].deviceId);
      }
    } catch (error: any) {
      console.error("❌ [CONFIG] Error obtaining microphones:", error);
      console.error("❌ [CONFIG] Error name:", error.name);
      console.error("❌ [CONFIG] Error message:", error.message);
      
      if (error.name === 'NotFoundError') {
        console.log("🎤 [CONFIG] NotFoundError - no microphones found");
        setMicrophoneErrorOpen(true);
      } else if (error.name === 'NotAllowedError') {
        console.log("🎤 [CONFIG] NotAllowedError - permission denied");
        setMicrophoneErrorOpen(true);
      } else {
        console.log("🎤 [CONFIG] Other error - treating as no microphone");
        setMicrophoneErrorOpen(true);
      }
      setAvailableMicrophones([]);
    }
  }, [selectedMicrophone, onMicrophoneChange]);

  const handleMicrophoneNotFound = useCallback(() => {
    console.log("🎤 [CONFIG] handleMicrophoneNotFound called - opening dialog");
    setMicrophoneErrorOpen(true);
  }, []);

  const handleRetryMicrophone = useCallback(() => {
    console.log("🎤 [CONFIG] Retrying microphone access");
    setMicrophoneErrorOpen(false);
    getMicrophones();
  }, [getMicrophones]);

  const handleTestMicrophone = useCallback(() => {
    console.log("🎤 [CONFIG] TEST BUTTON CLICKED - forcing microphone test");
    if (availableMicrophones.length === 0) {
      console.log("🎤 [CONFIG] No microphones available for testing - opening dialog");
      setMicrophoneErrorOpen(true);
      return;
    }
    
    // Start recording to test
    clearTranscription();
    startRecording();
  }, [availableMicrophones.length]);

  console.log("🎤 [CONFIG] Current state:", {
    availableMicrophones: availableMicrophones.length,
    selectedMicrophone,
    microphoneErrorOpen,
    handleMicrophoneNotFound: !!handleMicrophoneNotFound
  });

  const {
    startRecording,
    stopRecording,
    isRecording,
    transcriptionText,
    clearTranscription,
    connectionStatus,
    error
  } = useDeepgram({
    apiKey: DEEPGRAM_API_KEY,
    onTranscriptionReceived: (transcription) => {
      console.log('🎤 [CONFIG] Nueva transcripción:', transcription);
    },
    onError: (error) => {
      console.error('🎤 [CONFIG] Error de transcripción:', error);
    },
    onMicrophoneNotFound: handleMicrophoneNotFound
  });

  useEffect(() => {
    getMicrophones();
  }, [getMicrophones]);

  const toggleRecording = () => {
    console.log("🎤 [CONFIG] Toggle recording clicked, isRecording:", isRecording);
    
    if (availableMicrophones.length === 0) {
      console.log("🎤 [CONFIG] No microphones available - opening error dialog");
      setMicrophoneErrorOpen(true);
      return;
    }
    
    if (isRecording) {
      stopRecording();
    } else {
      clearTranscription();
      startRecording();
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'recording':
        return t('recording');
      case 'connecting':
        return t('connecting');
      case 'connected':
        return t('connected');
      case 'error':
        return t('connection_error');
      default:
        return t('stopped');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">{t('microphone_configuration')}</h2>
      <p className="text-sm text-gray-600">
        {t('microphone_configuration_description')}
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          {availableMicrophones.length === 0 ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">No se encontraron micrófonos disponibles.</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRetryMicrophone}
                className="mt-2"
              >
                Buscar micrófonos
              </Button>
            </div>
          ) : (
            <RadioGroup value={selectedMicrophone} onValueChange={onMicrophoneChange} className="space-y-3">
              {availableMicrophones.map((microphone) => (
                <div key={microphone.deviceId} className="flex items-center space-x-3">
                  <RadioGroupItem value={microphone.deviceId} id={microphone.deviceId} />
                  <Label htmlFor={microphone.deviceId} className="text-sm font-medium">
                    {microphone.label || `${t('microphone')} ${microphone.deviceId.slice(0, 8)}`}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('microphone_test_description')}
          </p>
          <Textarea
            value={transcriptionText}
            readOnly
            placeholder={t('transcription_placeholder')}
            className="h-24 resize-none bg-gray-50 border-gray-200"
          />
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className={`w-10 h-10 rounded-full ${
                isRecording 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={toggleRecording}
            >
              <Mic className="w-5 h-5" />
            </Button>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-75 ease-in-out ${
                  connectionStatus === 'recording' ? 'bg-green-500' : 'bg-gray-400'
                }`}
                style={{ width: `${isRecording ? '60' : '0'}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 min-w-[80px]">
              {getStatusText()}
            </span>
          </div>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      </div>
      
      <MicrophoneErrorDialog
        open={microphoneErrorOpen}
        onOpenChange={setMicrophoneErrorOpen}
        onRetry={handleRetryMicrophone}
      />
    </div>
  );
};
